// posts.ts — the blog series registry. Each entry is the whole post: metadata
// plus a markdown body. To add an essay, append an object here. The shell reads
// this array; nothing else needs to change.

export interface Post {
  readonly slug: string;
  readonly index: string;
  readonly title: string;
  readonly dek: string;
  readonly date: string;
  readonly body: string;
}

export const SERIES_TITLE = "Field Notes from Orbit";

export const POSTS: readonly Post[] = [
  {
    slug: "why-i-render-my-own-world",
    index: "01",
    title: "Why I render my own world",
    dek: "A blog that boots a GPU before it shows you a sentence.",
    date: "2026 · 06",
    body: `# Why I render my own world

Most websites are a stack of other people's decisions. A framework here, a
theme there, an engine that draws the pretty part. This one isn't.

Every pixel behind this text is computed by a fragment shader I wrote, fed by a
renderer I wrote, on top of WebGPU directly. No three.js, no scene graph I
didn't author. When the nebula drifts, that's *my* noise function folding in on
itself six times a frame.

## The point isn't performance

It's intimacy. When you own the whole pipeline you stop asking "how do I make
the library do this" and start asking "what do I actually want the light to do."
The answer becomes a few lines of WGSL instead of a config object.

> The medium is the message, and I wanted the medium to be hand-made.

So this is a blog series, but it's also a logbook of building the thing the blog
runs on. The renderer and the writing grow together.`,
  },
  {
    slug: "a-triangle-that-eats-the-screen",
    index: "02",
    title: "A triangle that eats the screen",
    dek: "The oldest trick in GPU graphics, and why it's still the right one.",
    date: "2026 · 06",
    body: `# A triangle that eats the screen

There is no geometry in this site. No meshes, no vertex buffers. The entire
visible world is one triangle — deliberately too big — stretched until it covers
the screen, with a shader run at every pixel inside it.

## Three vertices, no buffers

You don't even need to upload the triangle. The vertex shader invents it from
the vertex index:

\`\`\`wgsl
var p = array<vec2<f32>, 3>(
  vec2<f32>(-1.0, -1.0),
  vec2<f32>( 3.0, -1.0),
  vec2<f32>(-1.0,  3.0),
);
\`\`\`

Those coordinates reach past the clip box on two sides. The GPU clips the
overflow for free, and what's left is a perfect full-screen quad made of a
single primitive. One draw call, three vertices, infinite worlds.

Everything creative — the nebula, the stars, the warp when this reader opens —
lives in a single \`render(uv, frag)\` function. The engine's only job is to call
it once per pixel and get out of the way.`,
  },
  {
    slug: "making-it-feel-alive",
    index: "03",
    title: "Making it feel alive",
    dek: "Easing, parallax, and the small lies that make a screen breathe.",
    date: "2026 · 06",
    body: `# Making it feel alive

A static image is dead. The trick to a world that feels *present* isn't more
detail — it's motion that responds to you, slightly late, like it has weight.

## Never snap, always ease

When your cursor moves, the field doesn't jump to follow it. It eases:

\`\`\`ts
const k = 1 - Math.exp(-rate * dt);
current += (target - current) * k;
\`\`\`

That \`exp\` makes the smoothing frame-rate independent, so it feels identical on
a 60Hz laptop and a 120Hz phone. Every input — pointer, scroll, the warp when
you open an essay — flows through that same easing. Nothing in this site moves
in a straight line to where it's going.

## Parallax is a cheap lie that always works

Two layers of stars, drifting at different speeds against the nebula, and your
brain invents depth that was never there. The whole illusion is one multiply.

More notes as the renderer grows. This is entry three of an open-ended series —
the world and the writing are both still under construction.`,
  },
];

export const postBySlug = (slug: string): Post | undefined =>
  POSTS.find((p) => p.slug === slug);
